import React, { useState, useEffect } from 'react';
import { seoAPI, schedulingAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Search, AlertTriangle, CheckCircle, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSEO() {
  const [scores, setScores] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [seoRes, schedRes] = await Promise.all([
        seoAPI.scores({ limit: 30 }),
        schedulingAPI.list().catch(() => ({ data: { articles: [] } }))
      ]);
      setScores(seoRes.data.articles || []);
      setScheduled(schedRes.data.articles || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const publishScheduled = async () => {
    try {
      const res = await schedulingAPI.checkAndPublish();
      toast.success(res.data.message);
      loadData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-seo-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Search className="w-6 h-6 text-[#B89B5E]" /> SEO & Scheduling</h1>
        <p className="text-sm text-[#5B616E] mt-1">Optimize content for search engines and manage scheduling</p>
      </div>

      {/* Scheduled Articles */}
      {scheduled.length > 0 && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4" /> Scheduled Articles ({scheduled.length})</CardTitle>
            <Button size="sm" onClick={publishScheduled} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Zap className="w-3 h-3 mr-1" /> Publish Due</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scheduled.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#FBF7EF] text-sm">
                  <Clock className="w-4 h-4 text-[#9A6B1A]" />
                  <span className="flex-1 truncate">{a.title}</span>
                  <span className="text-xs text-[#5B616E]">{a.scheduled_at ? new Date(a.scheduled_at).toLocaleString() : 'No date'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SEO Scores */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm font-medium">Article SEO Scores</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : scores.length > 0 ? (
            <div className="space-y-3">
              {scores.map(a => (
                <div key={a.id} className="p-3 rounded-lg border border-[#E7E0D6] hover:bg-[#FBF7EF] transition-colors" data-testid="seo-score-item">
                  <div className="flex items-center gap-3">
                    {a.score >= 70 ? <CheckCircle className="w-5 h-5 text-[#1F7A5A]" /> : <AlertTriangle className="w-5 h-5 text-[#9A6B1A]" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={a.score} className="h-1.5 flex-1" />
                        <span className="text-xs font-medium" style={{ color: a.score >= 70 ? '#1F7A5A' : a.score >= 40 ? '#9A6B1A' : '#B3261E' }}>{a.score}%</span>
                      </div>
                    </div>
                  </div>
                  {a.issues?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {a.issues.map((issue, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] text-[#9A6B1A]">{issue}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#5B616E]"><p>No published articles to score</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
